import { LABEL_COLORS, type LabelColor } from './tasks.js';

/**
 * ADR 0134 (Briefing Paket C): fachliche Farbwerte – entweder ein Palettenname (behält seine Hell-/Dunkelvarianten)
 * oder eine eigene, normalisierte sechsstellige HEX-Farbe (`#146B62`). Keine CSS-Ausdrücke, Namen oder URLs.
 */
export type HexColor = `#${string}`;
export type ColorValue = LabelColor | HexColor;
export const HEX_COLOR = /^#[0-9A-F]{6}$/;

export const isPaletteColor = (c: unknown): c is LabelColor => typeof c === 'string' && (LABEL_COLORS as readonly string[]).includes(c);
export const isHexColor = (c: unknown): c is HexColor => typeof c === 'string' && HEX_COLOR.test(c);
export const isColorValue = (c: unknown): c is ColorValue => isPaletteColor(c) || isHexColor(c);

/** Eingabe normalisieren: Palettenname bleibt, HEX mit oder ohne `#` wird zu `#RRGGBB` (Großbuchstaben); sonst null */
export function normalizeColor(input: string | null | undefined): ColorValue | null {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (isPaletteColor(s)) return s;
  const h = s.startsWith('#') ? s.slice(1) : s;
  return /^[0-9a-fA-F]{6}$/.test(h) ? (`#${h.toUpperCase()}` as HexColor) : null;
}

/** Repräsentative HEX-Werte der Palette (für Lesbarkeit, Exporte und den Wähler) */
export const PALETTE_HEX: Record<LabelColor, HexColor> = {
  red: '#EF4444',
  orange: '#F97316',
  amber: '#F59E0B',
  green: '#16A34A',
  teal: '#0D9488',
  blue: '#2563EB',
  violet: '#7C3AED',
  pink: '#EC4899',
  gray: '#71717A',
};
export const toHex = (c: ColorValue): HexColor => (isPaletteColor(c) ? PALETTE_HEX[c] : c);

/** Relative Leuchtdichte nach WCAG 2.x */
export function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}
/** Lesbare Schrift auf einer Fläche dieser Farbe */
export const readableOn = (hex: string): '#000000' | '#FFFFFF' => (contrastRatio(hex, '#000000') >= contrastRatio(hex, '#FFFFFF') ? '#000000' : '#FFFFFF');
/** Taugt die Farbe als Schrift auf diesem Hintergrund (≥ 4,5:1)? Sonst nur als Marker zeigen, Text neutral lassen */
export const readableAsText = (hex: string, background: string) => contrastRatio(hex, background) >= 4.5;
